import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';

export interface CodeConfirmationConfig {
  title: string;
  message: string;
  expectedCode: string;
  codeLabel?: string;
  confirmButtonLabel?: string;
  cancelButtonLabel?: string;
  confirmButtonIcon?: string;
  severity?: 'success' | 'info' | 'warn' | 'danger';
}

export interface CodeConfirmationResult {
  confirmed: boolean;
  enteredCode?: string;
}

/**
 * Servicio para gestionar diálogos de confirmación con código
 * Proporciona una API simple y programática para mostrar confirmaciones críticas
 *
 * Uso:
 * ```typescript
 * const result = await this.codeConfirmationService.confirm({
 *   title: 'Eliminar Elemento',
 *   message: 'Esta acción eliminará el elemento permanentemente.',
 *   expectedCode: item.code,
 *   codeLabel: 'Código de Elemento',
 *   severity: 'danger'
 * });
 *
 * if (result.confirmed) {
 *   // Proceder con la acción
 * }
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class CodeConfirmationService {
  private currentConfig: CodeConfirmationConfig | null = null;
  private isVisible = false;

  /**
   * Resolutor de la ÚNICA confirmación en curso. Cada `confirm()` es dueño de su
   * propia promesa: no se comparte un Subject multicast (eso acumulaba suscripciones
   * huérfanas y hacía que un solo click resolviera N promesas → N llamadas al API,
   * BUG 167457). Solo puede haber una confirmación pendiente a la vez.
   */
  private pendingResolve: ((result: CodeConfirmationResult) => void) | null = null;

  // Observables para el estado del diálogo
  get config$(): Observable<CodeConfirmationConfig | null> {
    return this.configSubject.asObservable();
  }

  get visible$(): Observable<boolean> {
    return this.visibleSubject.asObservable();
  }

  private configSubject = new Subject<CodeConfirmationConfig | null>();
  private visibleSubject = new Subject<boolean>();

  /**
   * Muestra un diálogo de confirmación con código
   * @param config Configuración del diálogo
   * @returns Promise que se resuelve con el resultado de la confirmación
   */
  confirm(config: CodeConfirmationConfig): Promise<CodeConfirmationResult> {
    // Si quedó un diálogo anterior sin resolver (p. ej. se reabrió sin confirmar),
    // ciérralo como cancelado para no dejar la promesa colgada.
    this.settlePending({ confirmed: false });

    return new Promise((resolve) => {
      this.pendingResolve = resolve;

      // Configurar el diálogo
      this.currentConfig = {
        codeLabel: 'Código',
        confirmButtonLabel: 'Confirmar',
        cancelButtonLabel: 'Cancelar',
        confirmButtonIcon: 'pi pi-check',
        severity: 'warn',
        ...config
      };

      // Mostrar el diálogo
      this.configSubject.next(this.currentConfig);
      this.isVisible = true;
      this.visibleSubject.next(true);
    });
  }

  /**
   * Método específico para cancelación de reservas
   * @param pnr PNR de la reserva a cancelar
   * @param reservationDetails Detalles adicionales de la reserva (opcional)
   * @returns Promise con el resultado de la confirmación
   */
  confirmCancellation(pnr: string, reservationDetails?: string): Promise<CodeConfirmationResult> {
    const config: CodeConfirmationConfig = {
      title: 'Cancelar Reserva',
      message: `Esta acción cancelará permanentemente la reserva${reservationDetails ? ` para ${reservationDetails}` : ''}. Una vez cancelada, no podrá ser reactivada.`,
      expectedCode: pnr,
      codeLabel: 'Código de Reserva (PNR)',
      confirmButtonLabel: 'Cancelar Reserva',
      cancelButtonLabel: 'Mantener Activa',
      confirmButtonIcon: 'pi pi-times',
      severity: 'danger'
    };

    return this.confirm(config);
  }

  /**
   * Método específico para eliminación permanente
   * @param code Código del elemento a eliminar
   * @param itemType Tipo de elemento (reserva, pago, etc.)
   * @returns Promise con el resultado de la confirmación
   */
  confirmDeletion(code: string, itemType: string = 'registro'): Promise<CodeConfirmationResult> {
    const config: CodeConfirmationConfig = {
      title: `Eliminar ${itemType}`,
      message: `Esta acción eliminará permanentemente este ${itemType} y todos sus datos asociados. Esta operación no se puede deshacer.`,
      expectedCode: code,
      codeLabel: `Código de ${itemType}`,
      confirmButtonLabel: 'Eliminar Permanentemente',
      cancelButtonLabel: 'Conservar',
      confirmButtonIcon: 'pi pi-trash',
      severity: 'danger'
    };

    return this.confirm(config);
  }

  /**
   * Método específico para modificaciones críticas
   * @param code Código del elemento a modificar
   * @param action Descripción de la acción
   * @returns Promise con el resultado de la confirmación
   */
  confirmCriticalAction(code: string, action: string): Promise<CodeConfirmationResult> {
    const config: CodeConfirmationConfig = {
      title: 'Confirmar Acción Crítica',
      message: `Está a punto de ejecutar: ${action}. Esta operación puede tener efectos irreversibles.`,
      expectedCode: code,
      codeLabel: 'Código de Confirmación',
      confirmButtonLabel: 'Proceder',
      cancelButtonLabel: 'Cancelar',
      confirmButtonIcon: 'pi pi-exclamation-triangle',
      severity: 'warn'
    };

    return this.confirm(config);
  }

  /**
   * Oculta el diálogo actual
   */
  hide(): void {
    this.isVisible = false;
    this.visibleSubject.next(false);
    this.currentConfig = null;
    this.configSubject.next(null);
    // Descartar el diálogo (ESC / máscara / X) equivale a cancelar: resuelve el
    // pendiente para que ninguna promesa quede colgada.
    this.settlePending({ confirmed: false });
  }

  /**
   * Maneja la confirmación desde el componente
   * @param result Resultado de la confirmación
   */
  handleConfirmation(result: CodeConfirmationResult): void {
    this.settlePending(result);
    this.hide();
  }

  /**
   * Maneja la cancelación desde el componente
   */
  handleCancellation(): void {
    this.settlePending({ confirmed: false });
    this.hide();
  }

  /**
   * Resuelve (una sola vez) la confirmación pendiente y limpia la referencia.
   * Idempotente: si no hay nada pendiente, no hace nada.
   */
  private settlePending(result: CodeConfirmationResult): void {
    const resolve = this.pendingResolve;
    this.pendingResolve = null;
    resolve?.(result);
  }

  /**
   * Obtiene el estado actual de visibilidad
   */
  get isDialogVisible(): boolean {
    return this.isVisible;
  }

  /**
   * Obtiene la configuración actual
   */
  get currentConfiguration(): CodeConfirmationConfig | null {
    return this.currentConfig;
  }
}
